/**
 * Node Positioning Utilities
 * 
 * Shared logic for calculating positions for new nodes in the Flow view.
 * Used by:
 * - Task creation (table view, kanban view, flow view)
 * - Campaign nesting (sidebar drag-drop)
 * - Any future feature that creates missions
 * 
 * The goal is to always place new nodes in a sensible position that looks
 * good in Flow view without requiring manual repositioning.
 */

// Layout constants - these should match the Flow view's expectations
export const LAYOUT_PADDING = 40;      // Starting position for first node
export const NODE_WIDTH = 320;         // Default node width (matches TaskNode DEFAULT_WIDTH)
export const NODE_HEIGHT = 100;        // Default node height
export const HORIZONTAL_GAP = 40;      // Space between nodes horizontally
export const VERTICAL_GAP = 40;        // Space between nodes vertically

/**
 * Calculate the next available position for a new node in the flowchart.
 * 
 * Strategy: Place new nodes at the TOP level (y = PADDING), to the right of 
 * all existing nodes. This ensures new nodes appear in a clean, sensible 
 * position in Flow view regardless of where they were created.
 * 
 * @param {Array} tasks - Array of existing tasks with position data
 * @param {Object} nodePositions - Optional object mapping task IDs to {x, y} positions
 *                                 (used when positions aren't stored on tasks directly)
 * @returns {{x: number, y: number}} The calculated position for the new node
 * 
 * @example
 * // Using tasks with embedded positions
 * const pos = calculateNextNodePosition(sprint.tasks);
 * 
 * @example
 * // Using separate nodePositions object
 * const pos = calculateNextNodePosition(sprint.tasks, sprint.nodePositions);
 */
export function calculateNextNodePosition(tasks, nodePositions = null) {
  if (!tasks || tasks.length === 0) {
    // First node goes at the standard starting position
    return { x: LAYOUT_PADDING, y: LAYOUT_PADDING };
  }
  
  // Find the rightmost edge of all existing nodes
  let maxRightEdge = LAYOUT_PADDING;
  
  for (const task of tasks) {
    // Get position: check nodePositions object first, then fall back to task's embedded position
    // This handles both storage patterns used in the codebase
    let x = null;
    
    if (nodePositions && nodePositions[task.id]) {
      x = nodePositions[task.id].x;
    } else if (task.x !== undefined && task.x !== null) {
      x = task.x;
    }
    
    // Only consider tasks that have positions
    if (x !== null) {
      // Use task's actual width if it was resized, otherwise default
      const taskWidth = task.width || NODE_WIDTH;
      const rightEdge = x + taskWidth;
      if (rightEdge > maxRightEdge) {
        maxRightEdge = rightEdge;
      }
    }
  }
  
  // Place new node to the right of all existing nodes, at the top
  // Add horizontal gap to ensure proper spacing
  return {
    x: maxRightEdge + HORIZONTAL_GAP,
    y: LAYOUT_PADDING,
  };
}

/**
 * Calculate position for a node that should appear near a specific point.
 * Useful for creating connected nodes or inserting nodes near existing ones.
 * 
 * @param {number} nearX - X coordinate to place near
 * @param {number} nearY - Y coordinate to place near
 * @param {Array} tasks - Existing tasks to avoid overlapping
 * @param {Object} nodePositions - Optional separate positions object
 * @param {string} direction - 'right' | 'below' | 'left' | 'above'
 * @returns {{x: number, y: number}} The calculated position
 */
export function calculatePositionNear(nearX, nearY, tasks, nodePositions = null, direction = 'right') {
  const offsets = {
    right: { x: NODE_WIDTH + HORIZONTAL_GAP, y: 0 },
    below: { x: 0, y: NODE_HEIGHT + VERTICAL_GAP },
    left: { x: -(NODE_WIDTH + HORIZONTAL_GAP), y: 0 },
    above: { x: 0, y: -(NODE_HEIGHT + VERTICAL_GAP) },
  };
  
  const offset = offsets[direction] || offsets.right;
  
  return {
    x: Math.max(LAYOUT_PADDING, nearX + offset.x),
    y: Math.max(LAYOUT_PADDING, nearY + offset.y),
  };
}

/**
 * Check if two rectangles overlap (with optional padding).
 * 
 * @param {{x: number, y: number, width?: number, height?: number}} rect1 
 * @param {{x: number, y: number, width?: number, height?: number}} rect2 
 * @param {number} padding - Extra spacing to consider as overlap
 * @returns {boolean} True if rectangles overlap
 */
export function rectsOverlap(rect1, rect2, padding = 0) {
  const w1 = rect1.width || NODE_WIDTH;
  const h1 = rect1.height || NODE_HEIGHT;
  const w2 = rect2.width || NODE_WIDTH;
  const h2 = rect2.height || NODE_HEIGHT;
  
  return !(
    rect1.x + w1 + padding < rect2.x ||
    rect2.x + w2 + padding < rect1.x ||
    rect1.y + h1 + padding < rect2.y ||
    rect2.y + h2 + padding < rect1.y
  );
}
